import { LockClosedIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { ArrowGlyph, delay, tone } from '../../primitives';

export type SiteChatLine =
  | { from: 'visitor'; text: ReactNode; at: number }
  | {
      from: 'agent';
      /** Plain text renders as a bubble; anything else renders as given. */
      content: ReactNode;
      at: number;
      typingAt?: number;
    };

/**
 * A website built on the platform the page is about, with the agent's chat
 * open over it: the business's own site, our widget in its colours.
 */
export default function SiteChatDemo({
  brand,
  url,
  site,
  lines,
}: {
  brand: DemoBrand;
  /** What the address bar shows. */
  url: string;
  site: {
    headline: string;
    /** Tailwind classes for the hero picture of the site. */
    picture: string;
    /** Tailwind text class for the site's accent. */
    accent: string;
  };
  lines: SiteChatLine[];
}) {
  const { t: tc } = useLocale('chatbots');

  return (
    <div className='relative pb-24 sm:pb-20'>
      <div className='overflow-hidden rounded-[18px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
        <div className='flex items-center gap-3 border-b border-black/[0.06] bg-[#f6f6f9] px-3.5 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.04]'>
          <span className='flex gap-1.5' aria-hidden='true'>
            <span className='h-2.5 w-2.5 rounded-full bg-[#ff5f57]' />
            <span className='h-2.5 w-2.5 rounded-full bg-[#febc2e]' />
            <span className='h-2.5 w-2.5 rounded-full bg-[#28c840]' />
          </span>
          <span
            className={clsxm(
              'flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] dark:bg-white/[0.06]',
              tone.quiet,
            )}
          >
            <LockClosedIcon className='h-3 w-3 flex-none' />
            <span dir='ltr' className='truncate'>
              {url}
            </span>
          </span>
        </div>
        <div className='px-4 pb-24 pt-3.5'>
          <div className='flex items-center justify-between gap-3'>
            <span
              className={clsxm(
                'truncate text-[13px] font-bold tracking-tight',
                site.accent,
              )}
            >
              {brand.name}
            </span>
            <span className='flex flex-none gap-2' aria-hidden='true'>
              <span className='h-1.5 w-8 rounded-full bg-black/10 dark:bg-white/15' />
              <span className='h-1.5 w-8 rounded-full bg-black/10 dark:bg-white/15' />
              <span className='h-1.5 w-8 rounded-full bg-black/10 dark:bg-white/15' />
            </span>
          </div>
          <div
            className={clsxm(
              'mt-3 flex h-28 items-end rounded-xl p-3.5',
              site.picture,
            )}
          >
            <p className='max-w-[48%] text-[17px] font-bold leading-tight text-white drop-shadow-sm'>
              {site.headline}
            </p>
          </div>
          <div className='mt-3 space-y-1.5' aria-hidden='true'>
            <span className='block h-1.5 w-5/12 rounded-full bg-black/[0.07] dark:bg-white/10' />
            <span className='block h-1.5 w-4/12 rounded-full bg-black/[0.07] dark:bg-white/10' />
            <span className='block h-1.5 w-[38%] rounded-full bg-black/[0.07] dark:bg-white/10' />
          </div>
          <div className='mt-4 grid w-1/2 grid-cols-2 gap-2' aria-hidden='true'>
            <span className='h-12 rounded-lg bg-black/[0.05] dark:bg-white/[0.06]' />
            <span className='h-12 rounded-lg bg-black/[0.05] dark:bg-white/[0.06]' />
          </div>
        </div>
      </div>

      <div className='absolute bottom-0 end-2 w-[16.5rem] sm:end-[-1.25rem]'>
        <div className='overflow-hidden rounded-[18px] bg-white text-start shadow-[0_30px_70px_-20px_rgba(15,10,60,0.55)] ring-1 ring-black/5 dark:bg-[#1c1b27] dark:ring-white/10'>
          <div
            className={clsxm(
              'flex items-center gap-2.5 px-3.5 py-3 text-white',
              brand.bg,
            )}
          >
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/20 text-[12px] font-semibold'>
              {brand.initial}
            </span>
            <span className='min-w-0'>
              <span className='block truncate text-[13px] font-semibold'>
                {brand.name}
              </span>
              <span className='flex items-center gap-1 text-[10.5px] text-white/80'>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-300' />
                {tc('Replies instantly')}
              </span>
            </span>
          </div>
          <div className='space-y-2 px-3 py-3 text-[12.5px] leading-snug'>
            {lines.map((line, index) =>
              line.from === 'visitor' ? (
                <div key={index} className='flex justify-end'>
                  <p
                    className={clsxm(
                      styles.bubble,
                      styles.bubbleEnd,
                      'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-3 py-2 dark:bg-white/[0.08]',
                      tone.ink,
                    )}
                    style={delay(line.at)}
                  >
                    {line.text}
                  </p>
                </div>
              ) : (
                <div key={index} className='grid'>
                  {line.typingAt !== undefined && (
                    <span
                      className={clsxm(
                        styles.typing,
                        'flex h-7 w-12 items-center justify-center gap-1 self-start rounded-2xl rounded-es-md bg-[#f1f1f5] [grid-area:1/1] dark:bg-white/[0.08]',
                      )}
                      style={delay(line.typingAt, {
                        '--len': `${line.at - line.typingAt}ms`,
                      })}
                    >
                      <span className='h-1 w-1 rounded-full bg-[#8a8aa1]' />
                      <span className='h-1 w-1 rounded-full bg-[#8a8aa1]' />
                      <span className='h-1 w-1 rounded-full bg-[#8a8aa1]' />
                    </span>
                  )}
                  <div
                    className={clsxm(
                      styles.bubble,
                      styles.bubbleStart,
                      'flex min-w-0 [grid-area:1/1]',
                    )}
                    style={delay(line.at)}
                  >
                    {typeof line.content === 'string' ? (
                      <p
                        className={clsxm(
                          'max-w-[88%] rounded-2xl rounded-es-md px-3 py-2 text-white',
                          brand.bg,
                        )}
                      >
                        {line.content}
                      </p>
                    ) : (
                      line.content
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
          <div className='px-3 pb-3'>
            <div className='flex items-center gap-2 rounded-full bg-[#f3f3f7] py-2 pe-2 ps-3.5 dark:bg-white/[0.06]'>
              <span className={clsxm('flex-1 truncate text-xs', tone.quiet)}>
                {tc('Ask anything…')}
              </span>
              <span
                className={clsxm(
                  'flex h-6 w-6 items-center justify-center rounded-full text-white',
                  brand.bg,
                )}
              >
                <ArrowGlyph className='h-3 w-3 -rotate-90 rtl:rotate-90' />
              </span>
            </div>
          </div>
        </div>
        <span
          className={clsxm(
            'ms-auto mt-3 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-[0_14px_30px_-10px_rgba(15,10,60,0.6)]',
            brand.bg,
          )}
          aria-hidden='true'
        >
          <svg
            viewBox='0 0 24 24'
            className='h-6 w-6'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M6 18l-2 3V6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H8z'
            />
          </svg>
        </span>
      </div>
    </div>
  );
}

/** A button the agent shows in the website chat (the Custom Button action). */
export function ChatButton({
  brand,
  label,
}: {
  brand: DemoBrand;
  label: string;
}) {
  return (
    <span
      className={clsxm(
        'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm',
        brand.bg,
      )}
    >
      {label}
      <ArrowGlyph className='h-3 w-3' />
    </span>
  );
}
